import React, { useState, useRef } from 'react';
import { Modal, Form, Input, Select, InputNumber, message, Segmented, Button, Space, Divider } from 'antd';
import { ThunderboltOutlined, SoundOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { GenerateFromPropertiesRequest } from '../../types';

const { TextArea } = Input;
const { Option } = Select;

interface GeneratedAudioData {
  url: string;
  name: string;
  description: string | null;
  type: string;
  duration: number;
  fileSize: number;
  format: string;
  bpm: number;
  mood: string[];
  flow: string | null;
  instruments: string[];
  genre: string;
  layering: string;
}

interface GenerateAudioModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GenerateAudioModal: React.FC<GenerateAudioModalProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'basic' | 'advanced'>('basic');
  const [basicPrompt, setBasicPrompt] = useState('');
  const [parsing, setParsing] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState<GeneratedAudioData | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const durationValue = Form.useWatch('duration', form);

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();
      setGenerating(true);

      const data: GenerateFromPropertiesRequest = {
        name: values.name,
        description: values.description || undefined,
        type: 'music',
        instruments: values.instruments,
        genre: values.genre,
        moodAndTone: values.moodAndTone,
        tempo: values.tempo,
        layering: values.layering,
        flow: values.flow || undefined,
        duration: values.duration,
        draft: true,
      };

      const result = await audioLibraryApi.generateFromProperties(data);
      const audio = result.data || result;
      setGeneratedAudio({
        url: audio.url,
        name: audio.name,
        description: audio.description,
        type: audio.type,
        duration: audio.duration,
        fileSize: audio.fileSize,
        format: audio.format,
        bpm: audio.bpm,
        mood: audio.mood,
        flow: audio.flow,
        instruments: audio.instruments,
        genre: audio.genre,
        layering: audio.layering,
      });
      message.success('Audio generated! Listen to preview before saving.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to generate audio: ' + errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedAudio) return;
    try {
      setSaving(true);
      await audioLibraryApi.saveGeneratedAudio({
        url: generatedAudio.url,
        name: generatedAudio.name,
        description: generatedAudio.description || undefined,
        type: (generatedAudio.type as 'music' | 'sound_effect') || 'music',
        duration: generatedAudio.duration,
        fileSize: generatedAudio.fileSize,
        format: generatedAudio.format,
        bpm: generatedAudio.bpm,
        mood: generatedAudio.mood,
        flow: generatedAudio.flow || undefined,
        instruments: generatedAudio.instruments,
        genre: generatedAudio.genre,
        layering: generatedAudio.layering,
      });
      message.success('Audio saved to library!');
      handleReset();
      onSuccess();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to save audio: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setGeneratedAudio(null);
    handleGenerate();
  };

  const handleParsePrompt = async () => {
    if (!basicPrompt.trim()) {
      message.warning('Vui lòng nhập mô tả nhạc bạn muốn tạo');
      return;
    }
    try {
      setParsing(true);
      const result = await audioLibraryApi.parseAudioPrompt(basicPrompt);
      const data = result.data || result;
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        instruments: data.instruments,
        genre: data.genre,
        moodAndTone: data.moodAndTone,
        tempo: data.tempo,
        layering: data.layering,
        flow: data.flow,
        duration: data.duration || 60,
      });
      setMode('advanced');
      message.success('AI đã phân tích và điền vào form. Bạn có thể chỉnh sửa trước khi Generate.');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      message.error('Failed to parse prompt: ' + errorMsg);
    } finally {
      setParsing(false);
    }
  };

  const handleReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    form.resetFields();
    setBasicPrompt('');
    setMode('basic');
    setGeneratedAudio(null);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const moodOptions = [
    'uplifting', 'dramatic', 'tense', 'peaceful', 'sad', 'joyful',
    'mysterious', 'romantic', 'epic', 'melancholic', 'dark', 'energetic'
  ];

  const isBasic = mode === 'basic';

  return (
    <Modal
      title="Generate Audio from Properties"
      open={visible}
      onCancel={handleCancel}
      width={650}
      footer={
        generatedAudio ? (
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              icon={<ReloadOutlined />}
              loading={generating}
              disabled={saving}
              onClick={handleRegenerate}
            >
              Regenerate
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={generating}
              onClick={handleSave}
            >
              Save to Library
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={generating}
              disabled={isBasic}
              onClick={handleGenerate}
            >
              Generate
            </Button>
          </Space>
        )
      }
    >
      {generatedAudio && (
        <div style={{ marginBottom: 16, padding: 16, background: '#f0f5ff', borderRadius: 8, border: '1px solid #adc6ff' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space>
              <SoundOutlined style={{ color: '#1677ff', fontSize: 16 }} />
              <span style={{ fontWeight: 600 }}>Preview: {generatedAudio.name}</span>
            </Space>
            <audio
              ref={audioRef}
              controls
              src={generatedAudio.url}
              style={{ width: '100%' }}
            />
            <span style={{ fontSize: 12, color: '#666' }}>
              {generatedAudio.format?.toUpperCase()} · {Math.round(generatedAudio.duration)}s · {(generatedAudio.fileSize / 1024).toFixed(0)} KB
            </span>
          </Space>
        </div>
      )}

      <Segmented
        value={mode}
        onChange={(val) => setMode(val as 'basic' | 'advanced')}
        options={[
          { label: 'Basic', value: 'basic' },
          { label: 'Advanced', value: 'advanced' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {isBasic && (
        <div style={{ marginBottom: 16, padding: 16, background: '#f6f8fa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
          <TextArea
            rows={4}
            value={basicPrompt}
            onChange={e => setBasicPrompt(e.target.value)}
            placeholder="Mô tả nhạc bạn muốn tạo, ví dụ: 'Nhạc buồn, piano nhẹ nhàng, tempo chậm khoảng 80 BPM, phong cách cinematic, thời lượng 2 phút'"
            disabled={parsing}
          />
          <Space style={{ marginTop: 12 }}>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={parsing}
              onClick={handleParsePrompt}
            >
              Fill with AI
            </Button>
          </Space>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ tempo: 120, duration: 60 }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name for the audio' }]}
        >
          <Input placeholder="e.g., Epic Battle Theme" disabled={isBasic} />
        </Form.Item>

        <Form.Item name="description" label="Description (Optional)">
          <TextArea
            rows={2}
            placeholder="Describe the audio you want to generate"
            disabled={isBasic}
          />
        </Form.Item>

        <Form.Item
          name="duration"
          label="Duration"
          rules={[{ required: true, message: 'Please select duration' }]}
          extra={durationValue === 30 ? 'Will use clip model (30s max)' : `Will generate a longer song (model: lyria-3-pro-preview)`}
        >
          <Select style={{ width: '100%' }} disabled={isBasic}>
            <Option value={30}>30 seconds (clip)</Option>
            <Option value={45}>45 seconds</Option>
            <Option value={60}>1 minute</Option>
            <Option value={75}>1 minute 15 seconds</Option>
            <Option value={90}>1 minute 30 seconds</Option>
            <Option value={105}>1 minute 45 seconds</Option>
            <Option value={120}>2 minutes</Option>
            <Option value={135}>2 minutes 15 seconds</Option>
            <Option value={150}>2 minutes 30 seconds</Option>
            <Option value={165}>2 minutes 45 seconds</Option>
            <Option value={180}>3 minutes</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="instruments"
          label="Instruments"
          rules={[{ required: true, message: 'Please add at least one instrument' }]}
        >
          <Select
            mode="tags"
            placeholder="Add instruments (press Enter after each)"
            style={{ width: '100%' }}
            disabled={isBasic}
          >
            <Option value="Piano">Piano</Option>
            <Option value="Guitar">Guitar</Option>
            <Option value="Violin">Violin</Option>
            <Option value="Cello">Cello</Option>
            <Option value="Drums">Drums</Option>
            <Option value="Bass">Bass</Option>
            <Option value="Synthesizer">Synthesizer</Option>
            <Option value="Flute">Flute</Option>
            <Option value="Trumpet">Trumpet</Option>
            <Option value="Harp">Harp</Option>
            <Option value="Orchestra">Orchestra</Option>
            <Option value="Synth Pad">Synth Pad</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="genre"
          label="Genre"
          rules={[{ required: true, message: 'Please enter a genre' }]}
        >
          <Input placeholder="e.g., cinematic, electronic, orchestral, ambient" disabled={isBasic} />
        </Form.Item>

        <Form.Item
          name="moodAndTone"
          label="Mood and Tone"
          rules={[{ required: true, message: 'Please select or enter mood and tone' }]}
        >
          <Select
            mode="tags"
            placeholder="Select or type mood and tone"
            style={{ width: '100%' }}
            disabled={isBasic}
          >
            {moodOptions.map(mood => (
              <Option key={mood} value={mood}>{mood}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="tempo"
          label="Tempo (BPM)"
          rules={[{ required: true, message: 'Please enter tempo' }]}
        >
          <InputNumber min={40} max={300} style={{ width: '100%' }} disabled={isBasic} />
        </Form.Item>

        <Form.Item
          name="layering"
          label="Layering"
          rules={[{ required: true, message: 'Please describe the layering' }]}
        >
          <TextArea
            rows={2}
            placeholder="Describe the layering structure, e.g., 'Bắt đầu với piano nhẹ nhàng, dần dần thêm dây và bộ gõ'"
            disabled={isBasic}
          />
        </Form.Item>

        <Form.Item name="flow" label="Flow (Optional)">
          <TextArea
            rows={3}
            placeholder="Describe how the audio should progress over time"
            disabled={isBasic}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GenerateAudioModal;
